/**
 * RBACGate — Declarative permission-gating component (desktop / Tailwind).
 *
 * Functionally identical to BigxBookApp/src/rbac/RBACGate.tsx but renders
 * raw children without React Native wrappers. The hook + permission logic
 * underneath is byte-identical to mobile.
 */

import type { JSX, ReactNode } from 'react';
import { usePermissions } from './usePermissions';
import type { Permission } from './permissions';

interface RBACGateProps {
  /** Permission(s) required. String for single, array for multiple. */
  requires: Permission | Permission[];
  /** When true, ALL permissions in the array must be granted (AND logic).
   *  When false (default), ANY permission suffices (OR logic). */
  requireAll?: boolean;
  /** Content to render when the user lacks the required permissions.
   *  Defaults to null (hidden). */
  fallback?: ReactNode;
  /** Content to render when the user IS authorized. */
  children: ReactNode;
}

export function RBACGate({
  requires,
  requireAll = false,
  fallback = null,
  children,
}: RBACGateProps): JSX.Element | null {
  const { can, canAny, canAll } = usePermissions();

  const isGranted = Array.isArray(requires)
    ? requireAll
      ? canAll(requires)
      : canAny(requires)
    : can(requires);

  if (!isGranted) return fallback as JSX.Element | null;
  return <>{children}</>;
}
